/* eslint-disable react/destructuring-assignment */
import React, { useState, useEffect } from "react";
import { Checkbox, Divider, Dropdown } from "antd";
import { isEqual } from "lodash";
import { ReactComponent as SixDotsIcon } from "../../img/six-dots.svg";
import usePrevious from "../../lib/hooks/usePrevious";
import "./ToggleColumns.less";
import LocalStorageService from "../../services/LocalStorageService";

interface IProps {
  tableName: string;
  indexColHiddenForCheckAll?: number;
  notHiddenColumnIndex?: number; // Индекс колонки, которая будет оставаться открытой по нажатии на "Скрыть все". По умолчанию: 0
  columns: any[];
  setColumns: React.Dispatch<React.SetStateAction<any>>;
  settings?: { [key: string]: { name: string; value: any } };
  setSettings?: React.Dispatch<React.SetStateAction<any>>;
  setChildTableColumns?: React.Dispatch<React.SetStateAction<any>>;
  isButtonDisabled?: boolean;
  className?: string;
}

const ToggleColumns = (props: IProps) => {
  const {
    settings,
    setSettings,
    setColumns,
    tableName,
    setChildTableColumns,
    isButtonDisabled = false,
    notHiddenColumnIndex = 0,
    className,
  } = props;
  // Сделано для того, чтобы когда title - элемент, правильно выводилось наименование
  const columns = props.columns.map((column) => ({ ...column, title: column.title?.toString() }));
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const savedValues = LocalStorageService.getObject(`${tableName}_columns`);
  const parsedSavedValues = savedValues ? JSON.parse(savedValues) : null;
  const savedSettings = LocalStorageService.getObject(`${tableName}_settings`);
  const parsedSavedSettings = savedSettings ? JSON.parse(savedSettings) : null;
  const managedColumns = columns.filter((item) => item.title && item.dataIndex);
  const visibleColumns = managedColumns.filter((item) => !item.hidden);

  const getDefaultCheckAll = () => {
    if (!parsedSavedValues) {
      if (visibleColumns.length === managedColumns.length) {
        return true;
      }
      if (visibleColumns.length > 0) {
        return "some";
      }
      return false;
    }
    if (parsedSavedValues.length === managedColumns.length) {
      return true;
    }
    if (parsedSavedValues.length > 0) {
      return "some";
    }
    return false;
  };

  const [checkAll, setCheckAll] = useState<false | "some" | true>("some");

  useEffect(() => {
    setCheckAll(getDefaultCheckAll());
  }, [parsedSavedValues, managedColumns, visibleColumns]);

  const options = managedColumns.map((item) => {
    const isLastVisibleColumn = visibleColumns.length === 1;
    if (isLastVisibleColumn && !item.hidden) {
      return {
        label: item.title,
        value: item.dataIndex,
        disabled: true,
      };
    }
    return {
      label: item.title,
      value: item.dataIndex,
    };
  });

  const onChange = (checkedValues) => {
    setColumns((prevState) =>
      prevState.map((column) => {
        if (column.title && !checkedValues.includes(column.dataIndex)) {
          return {
            ...column,
            hidden: !column?.constantlyDisplayed,
          };
        }
        return { ...column, hidden: false };
      })
    );
    if (setChildTableColumns) {
      setChildTableColumns((prevState) =>
        prevState.map((column) => {
          if (column.title && !checkedValues.includes(column.dataIndex)) {
            return {
              ...column,
              hidden: !column?.constantlyDisplayed,
            };
          }
          return { ...column, hidden: false };
        })
      );
    }
    if (checkedValues.length === managedColumns.length) {
      setCheckAll(true);
    } else if (checkedValues.length > 0) {
      setCheckAll("some");
    } else {
      setCheckAll(false);
    }
    LocalStorageService.setObject(`${tableName}_columns`, JSON.stringify(checkedValues));
  };

  const onChangeAll = (checked: boolean) => {
    setColumns((prevState) => {
      if (checked) {
        return prevState.map((column) => {
          if (column.title && column.dataIndex) {
            return { ...column, hidden: false };
          }
          return column;
        });
      }
      // Оставляем одну колонку открытой при "снять все"
      return prevState.map((column, i) => {
        if (column.title && column.dataIndex) {
          const hidden = (() => {
            if (props.indexColHiddenForCheckAll) return i > props.indexColHiddenForCheckAll; // TODO Используется только в кросс-курсах. Решение костыльное. Переделать
            if (column?.constantlyDisplayed) return false;
            return i !== notHiddenColumnIndex;
          })();
          return { ...column, hidden };
        }
        return column;
      });
    });
    if (setChildTableColumns) {
      setChildTableColumns((prevState) => {
        if (checked) {
          return prevState.map((column) => {
            if (column.title && column.dataIndex) {
              return { ...column, hidden: false };
            }
            return column;
          });
        }
        // Оставляем одну колонку открытой при "снять все"
        return prevState.map((column, i) => {
          if (column.title && column.dataIndex) {
            const hidden = (() => {
              if (props.indexColHiddenForCheckAll) return i > props.indexColHiddenForCheckAll; // TODO Используется только в кросс-курсах. Решение костыльное. Переделать
              if (column?.constantlyDisplayed) return false;
              return i !== notHiddenColumnIndex;
            })();
            return { ...column, hidden };
          }
          return column;
        });
      });
    }
    setCheckAll(checked ? true : "some");
    const checkedValues = checked
      ? managedColumns.map(({ dataIndex }) => dataIndex)
      : // Оставляем одну колонку открытой при "снять все"
        [columns[notHiddenColumnIndex].dataIndex];
    LocalStorageService.setObject(`${tableName}_columns`, JSON.stringify(checkedValues));
  };

  const onChangeSettings = (changedSettings) => {
    if (setSettings) {
      setSettings((prev) => {
        const newSettings = { ...prev, ...changedSettings };
        LocalStorageService.setObject(`${tableName}_settings`, JSON.stringify(newSettings));
        return newSettings;
      });
    }
  };

  const [update, setUpdate] = useState(false);

  useEffect(() => {
    if (savedValues) {
      setColumns((prevState) => {
        const settedColumns = prevState.map((column) => {
          if (column.title && !parsedSavedValues.includes(column.dataIndex)) {
            return {
              ...column,
              hidden: !column?.constantlyDisplayed,
            };
          }
          return { ...column, hidden: false };
        });
        return settedColumns;
      });
      if (setChildTableColumns) {
        setChildTableColumns((prevState) =>
          prevState.map((column) => {
            if (column.title && !parsedSavedValues.includes(column.dataIndex)) {
              return {
                ...column,
                hidden: !column?.constantlyDisplayed,
              };
            }
            return { ...column, hidden: false };
          })
        );
      }
    }
  }, [update]);

  // Восстановление значений hidden при изменении других полей колонок
  const prevColumns: any[] = usePrevious(columns) || [];
  useEffect(() => {
    if (Array.isArray(prevColumns)) {
      const prevColumnsWithoutHidden = prevColumns.map(({ hidden, ...rest }) => rest);
      const columnsWithoutHidden = columns.map(({ hidden, ...rest }) => rest);
      if (!isEqual(prevColumnsWithoutHidden, columnsWithoutHidden)) {
        setUpdate((prev) => !prev);
      }
    }
  }, [columns]);

  useEffect(() => {
    if (parsedSavedSettings && setSettings) {
      setSettings(parsedSavedSettings);
    }
  }, []);

  const dropdownMenu = (
    <div className="toggle-columns_checkbox-group-container">
      {settings && setSettings && (
        <>
          <h4>Настройки</h4>
          <Checkbox
            checked={
              parsedSavedSettings ? parsedSavedSettings.fullDate.value : settings.fullDate.value
            }
            onChange={(e) =>
              onChangeSettings({ fullDate: { ...settings.fullDate, value: e.target.checked } })
            }
          >
            Полная дата
          </Checkbox>
          <Divider style={{ margin: "5px" }} />
        </>
      )}
      <h4>Столбцы</h4>
      <Checkbox
        onChange={(e) => onChangeAll(e.target.checked)}
        indeterminate={checkAll === "some"}
        checked={checkAll === true}
      >
        Выбрать / снять все
      </Checkbox>
      <Divider style={{ margin: "5px" }} />
      <Checkbox.Group
        className="checkbox-group"
        options={options}
        value={managedColumns.filter(({ hidden }) => !hidden).map(({ dataIndex }) => dataIndex)}
        onChange={onChange}
      />
    </div>
  );

  return (
    <div className={className}>
      <Dropdown
        trigger={["click"]}
        visible={isDropdownVisible}
        onVisibleChange={(visible: boolean) => setIsDropdownVisible(visible)}
        overlay={dropdownMenu}
        className="toggle-columns"
        overlayClassName="toggle-columns_dropdown"
        disabled={isButtonDisabled}
      >
        <SixDotsIcon
          style={{ cursor: isButtonDisabled ? "not-allowed" : "pointer" }}
          className="toggle-columns__dots-icon"
        />
      </Dropdown>
    </div>
  );
};

export default ToggleColumns;
